import { ClassMetadata } from './ClassMetadata';
import { TypeCache } from '.';
import { PropertyMetadata } from './PropertyMetadata';
var TypeMetadata = /** @class */ (function () {
    function TypeMetadata() {
        this.basicType = "unknown";
        /**
         * True if the type is nullable. This is only set for basic types.
         */
        this.isNullable = false;
        /**
         * If basic type is 'array' or 'object', this specifies the details of the object or array.
         * If null for 'array', check the arraySimpleType property.
         */
        this.customType = null;
        /**
         * If basicType is 'array' and customType is null, then this specifies the simple type the array contains. E.g. array of string.
         */
        this.arraySimpleType = null;
    }
    TypeMetadata.create = function (schema, cache) {
        if (cache) {
            var typeMetadata = new TypeMetadata();
            typeMetadata.parse(schema, cache);
            return typeMetadata;
        }
        else {
            return new TypeCache(schema).root;
        }
    };
    TypeMetadata.prototype.parse = function (schema, cache) {
        var _a;
        if (schema.items) {
            var innerType = TypeMetadata.create(schema.items, cache);
            if (innerType.basicType !== "unknown") {
                this.basicType = "array";
                this.customType = innerType.customType;
                this.arraySimpleType = innerType.customType === null ? innerType.basicType : null;
            }
        }
        else if (schema.$ref) {
            var refType = cache.getOrAddType(schema.$ref);
            this.basicType = refType.basicType;
            this.customType = refType.customType;
            this.arraySimpleType = refType.arraySimpleType;
        }
        else if (schema.oneOf) {
            var types = schema.oneOf;
            var basicType = types.find(function (c) { return c.type; });
            var refType = types.find(function (c) { return c.$ref; });
            if ((basicType === null || basicType === void 0 ? void 0 : basicType.type) === "null" && refType) {
                this.parse(refType, cache);
            }
        }
        else if (schema.allOf) {
            var types = schema.allOf;
            var basicType = types.find(function (c) { return c.type; });
            var refType = types.find(function (c) { return c.$ref; });
            if ((basicType === null || basicType === void 0 ? void 0 : basicType.type) === "object" && refType) {
                this.parse(refType, cache);
                this.customType = this.customType.clone();
                (_a = this.customType) === null || _a === void 0 ? void 0 : _a.loadMetaData(basicType, cache);
            }
        }
        else if (schema.type) {
            var simpleType = schema;
            if (simpleType.type === "object" || simpleType.type.indexOf("object") >= 0) {
                this.basicType = "object";
                this.customType = new ClassMetadata("");
                this.customType.loadMetaData(simpleType, cache);
            }
            else {
                var typeInfo = TypeMetadata.getType(simpleType.type);
                this.basicType = typeInfo.type;
                this.isNullable = typeInfo.isNullable;
                if (simpleType.format === "date-time") {
                    this.basicType = "date";
                    this.serialisedType = "string";
                }
            }
        }
        else {
            this.basicType = "unknown";
        }
    };
    /**
     * Get a basic type from a json schema string type.
     */
    TypeMetadata.getType = function (type) {
        if (typeof type === "string") {
            return { type: type, isNullable: false };
        }
        else {
            var isNullable = type.indexOf("null") >= 0;
            var basicType = void 0;
            if (type.indexOf("string") >= 0) {
                basicType = "string";
            }
            else if (type.indexOf("number") >= 0) {
                basicType = "number";
            }
            else if (type.indexOf("integer") >= 0) {
                basicType = "integer";
            }
            else if (type.indexOf("boolean") >= 0) {
                basicType = "boolean";
            }
            else {
                basicType = "unknown";
            }
            return { type: basicType, isNullable: isNullable };
        }
    };
    /**
     * Creates a type metadata object from information inferred from the data.
     * @param data Object / Array
     * @param parentKey The key to give to the root type.
     * @param maxDepth Max levels to recurse.
     */
    TypeMetadata.fromData = function (data, parentKey, maxDepth) {
        var type = new TypeMetadata();
        if (data instanceof Array) {
            if (data.length) {
                type.basicType = "array";
                var itemType = TypeMetadata.fromData(data[0], "", maxDepth);
                if (itemType.customType) {
                    type.customType = itemType.customType;
                }
                else {
                    type.arraySimpleType = itemType.basicType;
                }
            }
        }
        else if (data instanceof Date) {
            type.basicType = "date";
        }
        else if (typeof data === "number") {
            type.basicType = (parentKey.endsWith("Id") || parentKey.endsWith("ID")) ? "integer" : "number";
        }
        else if (typeof data === "boolean") {
            type.basicType = "boolean";
        }
        else if (typeof data === "string") {
            type.basicType = "string";
        }
        else if (data instanceof Object && maxDepth > 0) {
            type.basicType = "object";
            type.customType = new ClassMetadata(parentKey);
            for (var propertyKey in data) {
                var propertyType = TypeMetadata.fromData(data[propertyKey], propertyKey, maxDepth - 1);
                var propertyInfo = new PropertyMetadata(propertyKey, propertyType);
                type.customType.properties.push(propertyInfo);
                if (propertyType.basicType !== "unknown") {
                    if (propertyType.basicType === "array") {
                        type.customType.childListProperties.push(propertyInfo);
                    }
                    else if (propertyType.basicType === "object") {
                        type.customType.childObjectProperties.push(propertyInfo);
                    }
                    else {
                        type.customType.simpleProperties.push(propertyInfo);
                    }
                }
            }
        }
        return type;
    };
    return TypeMetadata;
}());
export { TypeMetadata };
